import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthStore } from '../store/authStore';
import type { AuthUser, SessionInfo } from '../models/v2/auth';

interface AuthContextType {
    user: AuthUser | null;
    isAuthenticated: boolean;
    login: (session: SessionInfo, user: AuthUser) => void;
    logout: () => void;
    updateUser: (user: AuthUser) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
    // Initialize state from AuthStore synchronously to prevent flash of guest content
    const [user, setUser] = useState<AuthUser | null>(() => AuthStore.getUser());
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => AuthStore.isAuthenticated());

    // Listen for storage events (optional, for multi-tab sync) or just relying on internal state
    // ideally we trust our internal state first.

    const login = (session: SessionInfo, userData: AuthUser) => {
        // 1. Persist to store
        AuthStore.setSession(session.session_id, session.expires_at, userData);

        // 2. Update React state
        setUser(userData);
        setIsAuthenticated(true);
    };

    const logout = () => {
        // 1. Clear store
        AuthStore.clearSession();

        // 2. Update React state
        setUser(null);
        setIsAuthenticated(false);
    };

    const updateUser = (userData: AuthUser) => {
        // Update user data in store without destroying session
        const sessionId = AuthStore.getSessionId();
        // We need expiresAt, if stored separately. AuthStore doesn't expose a getter for expiresAt raw.
        // Making an assumption here or we should add a getter to AuthStore.
        // For now, let's just update the user part of local storage manually or via a new Store method.
        // Let's rely on setSession taking all args. We can grab raw values from localStorage for now.

        const expiresAt = localStorage.getItem('auth_expires_at') || '';
        if (sessionId) {
            AuthStore.setSession(sessionId, expiresAt, userData);
        }

        setUser(userData);
    };

    return (
        <AuthContext.Provider value={{ user, isAuthenticated, login, logout, updateUser }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
